/**
 * Collection Toolbar behavior
 * - Toggles `is-stuck` while the toolbar is in its sticky state
 */

class CollectionToolbar {
  /**
   * @param {Document|HTMLElement} container
   */
  constructor(container = document) {
    this.container = container
    this.toolbars = []
    this.refreshToolbars()
    if (!this.toolbars.length) return

    this.rafScheduled = false
    this.onScroll = this.onScroll.bind(this)

    // Observe resizes on each toolbar (layout changes can affect stickiness)
    this.resizeObserver = new ResizeObserver(() => this.onScroll())
    this.toolbars.forEach((el) => this.resizeObserver.observe(el))

    // Observe DOM changes so we re-bind if the toolbar gets re-rendered
    this.mutationObserver = new MutationObserver(() => {
      const beforeCount = this.toolbars.length
      this.refreshToolbars()
      const afterCount = this.toolbars.length
      if (afterCount !== beforeCount) {
        this.onScroll()
      }
    })
    if (document.body) {
      this.mutationObserver.observe(document.body, { childList: true, subtree: true })
    }

    window.addEventListener('scroll', this.onScroll, { passive: true })
    window.addEventListener('resize', this.onScroll)

    // Initial evaluation
    this.onScroll()
  }

  onScroll() {
    if (this.rafScheduled) return
    this.rafScheduled = true
    requestAnimationFrame(() => {
      this.rafScheduled = false
      this.toolbars.forEach((el) => {
        const styles = getComputedStyle(el)
        // Prefer top; fall back to logical inset-block-start if needed (Safari)
        let stickyTop = parseFloat(styles.top)
        if (Number.isNaN(stickyTop)) {
          const logicalTop = styles.getPropertyValue('inset-block-start')
          stickyTop = parseFloat(logicalTop) || 0
        }
        const rectTop = el.getBoundingClientRect().top
        // Allow a small tolerance for subpixel differences across browsers
        const isStuck = Math.abs(Math.round(rectTop) - Math.round(stickyTop)) <= 1 && styles.position === 'sticky'
        el.classList.toggle('is-stuck', isStuck)
      })
    })
  }

  refreshToolbars() {
    const current = Array.from(this.container.querySelectorAll('.collection-toolbar'))
    // Unobserve removed elements
    if (this.resizeObserver && this.toolbars.length) {
      this.toolbars.forEach((el) => {
        if (!current.includes(el)) this.resizeObserver.unobserve(el)
      })
    }
    this.toolbars = current
    // Observe new elements
    if (this.resizeObserver && this.toolbars.length) {
      this.toolbars.forEach((el) => this.resizeObserver.observe(el))
    }
  }

  destroy() {
    window.removeEventListener('scroll', this.onScroll)
    window.removeEventListener('resize', this.onScroll)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.mutationObserver) this.mutationObserver.disconnect()
  }
}

export default CollectionToolbar


